<template>
  <div class="gm-chart-controls">
    <div class="gm-chart-header">
      <div class="gm-chart-title">
        <span class="gm-chart-icon">{{ icon }}</span>
        <span class="gm-chart-name">{{ title }}</span>
      </div>
      
      <div class="gm-chart-actions">
        <button 
          @click="toggleFilters" 
          class="gm-filter-toggle"
          :class="{ active: showFilters }"
        >
          <span class="gm-filter-icon">🔧</span>
          Filters
        </button>
        
        <button 
          @click="refreshChart" 
          class="gm-refresh-btn"
          :disabled="isLoading"
        >
          <span class="gm-refresh-icon" :class="{ spinning: isLoading }">🔄</span>
        </button>
        
        <button 
          @click="exportChart" 
          class="gm-export-btn"
        >
          <span class="gm-export-icon">📊</span>
        </button>
      </div>
    </div>
    
    <!-- Filters Panel -->
    <div v-if="showFilters" class="gm-filters-panel">
      <div class="gm-filters-content">
        <!-- Simple Date Range -->
        <div class="gm-filter-section">
          <label class="gm-filter-label">Date Range</label>
          <div class="gm-date-range-simple">
            <select v-model="selectedDateRange" class="gm-filter-select">
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_90_days">Last 90 days</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="this_quarter">This quarter</option>
              <option value="last_quarter">Last quarter</option>
              <option value="this_year">This year</option>
            </select>
          </div>
        </div>
        
        <!-- Channel Filter -->
        <div v-if="showAdditionalFilters" class="gm-filter-section">
          <label class="gm-filter-label">Channel</label>
          <select v-model="selectedChannel" class="gm-filter-select">
            <option value="">All Channels</option>
            <option v-for="channel in channels" :key="channel.id" :value="channel.id">
              {{ channel.name }}
            </option>
          </select>
        </div>
        
        <!-- Agent Filter -->
        <div v-if="showAdditionalFilters" class="gm-filter-section">
          <label class="gm-filter-label">Agent</label>
          <select v-model="selectedAgent" class="gm-filter-select">
            <option value="">All Agents</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </div>
        
        <!-- Apply Filters -->
        <div class="gm-filter-actions">
          <button 
            @click="applyFilters" 
            class="gm-apply-filters-btn"
            :disabled="isLoading"
          >
            Apply
          </button>
          
          <button 
            @click="resetFilters" 
            class="gm-reset-filters-btn"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
    
    <!-- Chart Info -->
    <div class="gm-chart-info">
      <div class="gm-chart-stats">
        <span class="gm-stat-item">
          <span class="gm-stat-label">Range:</span>
          <span class="gm-stat-value">{{ formattedDateRange }}</span>
        </span>
        <span v-if="lastUpdated" class="gm-stat-item">
          <span class="gm-stat-label">Updated:</span>
          <span class="gm-stat-value">{{ formattedLastUpdated }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Props
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    default: '📊'
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  lastUpdated: {
    type: String,
    default: null
  },
  showAdditionalFilters: {
    type: Boolean,
    default: false
  },
  channels: {
    type: Array,
    default: () => []
  },
  agents: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: Object,
    default: () => ({
      dateRange: { from: null, to: null, preset: 'last_30_days' },
      period: { type: 'month', value: null },
      channel: null,
      agent: null
    })
  }
});

// Emits
const emit = defineEmits(['update:modelValue', 'refresh', 'export', 'filters-change']);

// Reactive data
const showFilters = ref(false);
const selectedDateRange = ref('last_30_days');
const selectedChannel = ref('');
const selectedAgent = ref('');

// Computed
const formattedDateRange = computed(() => {
  const rangeLabels = {
    'last_7_days': 'Last 7 days',
    'last_30_days': 'Last 30 days',
    'last_90_days': 'Last 90 days',
    'this_month': 'This month',
    'last_month': 'Last month',
    'this_quarter': 'This quarter',
    'last_quarter': 'Last quarter',
    'this_year': 'This year'
  };
  
  return rangeLabels[selectedDateRange.value] || 'Last 30 days';
});

const formattedLastUpdated = computed(() => {
  if (!props.lastUpdated) return '';
  
  const date = new Date(props.lastUpdated);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
});

// Methods
const toggleFilters = () => {
  console.log('🔧 GM Chart Controls: Toggling filters, current state:', showFilters.value);
  showFilters.value = !showFilters.value;
  console.log('🔧 GM Chart Controls: New state:', showFilters.value);
};

const refreshChart = () => {
  emit('refresh');
};

const exportChart = () => {
  emit('export');
};

const applyFilters = () => {
  console.log('🔧 GM Chart Controls: Applying filters...');
  console.log('🔧 GM Chart Controls: Current filter values:', {
    selectedDateRange: selectedDateRange.value,
    selectedChannel: selectedChannel.value,
    selectedAgent: selectedAgent.value
  });
  
  const filters = {
    dateRange: { preset: selectedDateRange.value },
    channel: selectedChannel.value,
    agent: selectedAgent.value
  };
  
  console.log('🔧 GM Chart Controls: Constructed filters object:', filters);
  console.log('🔧 GM Chart Controls: Filters type:', typeof filters);
  console.log('🔧 GM Chart Controls: Filters keys:', Object.keys(filters));
  console.log('🔧 GM Chart Controls: Date range object:', filters.dateRange);
  console.log('🔧 GM Chart Controls: Date range preset:', filters.dateRange.preset);
  
  emit('update:modelValue', filters);
  emit('filters-change', filters);
  showFilters.value = false;
  console.log('🔧 GM Chart Controls: Filters applied and panel closed');
};

const resetFilters = () => {
  selectedDateRange.value = 'last_30_days';
  selectedChannel.value = '';
  selectedAgent.value = '';
  
  const filters = {
    dateRange: { preset: 'last_30_days' },
    channel: '',
    agent: ''
  };
  
  emit('update:modelValue', filters);
  emit('filters-change', filters);
};

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    selectedDateRange.value = newValue.dateRange?.preset || 'last_30_days';
    selectedChannel.value = newValue.channel || '';
    selectedAgent.value = newValue.agent || '';
  }
}, { immediate: true });
</script>

<style scoped>
.gm-chart-controls {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 1rem;
}

.gm-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.gm-chart-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-chart-icon {
  font-size: 1.25rem;
}

.gm-chart-name {
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
}

.gm-chart-actions {
  display: flex;
  gap: 0.5rem;
}

.gm-filter-toggle,
.gm-refresh-btn,
.gm-export-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.gm-filter-toggle:hover,
.gm-refresh-btn:hover,
.gm-export-btn:hover {
  background: #f9fafb;
  color: #374151;
}

.gm-filter-toggle.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.gm-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.gm-refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.gm-filters-panel {
  border-top: 1px solid #e5e7eb;
}

.gm-filters-content {
  padding: 1rem;
}

.gm-filter-section {
  margin-bottom: 1rem;
}

.gm-filter-section:last-child {
  margin-bottom: 0;
}

.gm-additional-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.gm-filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gm-filter-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.gm-filter-select {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
}

.gm-filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.gm-date-range-simple {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gm-filter-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.gm-apply-filters-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.gm-apply-filters-btn:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.gm-apply-filters-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.gm-reset-filters-btn {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.gm-reset-filters-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.gm-chart-info {
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}

.gm-chart-stats {
  display: flex;
  gap: 1.5rem;
  font-size: 0.875rem;
}

.gm-stat-item {
  display: flex;
  gap: 0.5rem;
}

.gm-stat-label {
  color: #6b7280;
  font-weight: 500;
}

.gm-stat-value {
  color: #374151;
  font-weight: 600;
}

@media (max-width: 768px) {
  .gm-chart-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .gm-chart-actions {
    justify-content: center;
  }
  
  .gm-additional-filters {
    grid-template-columns: 1fr;
  }
  
  .gm-chart-stats {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .gm-filter-actions {
    flex-direction: column;
  }
}
</style>
